import { CommonModule } from '@angular/common';
import { Component, OnInit, Optional } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { MainHeaderComponent } from '../../shared/main-header/main-header.component';
import { CreateSchemaRulePayload, SchemaRulesService } from '../../../services/schema-rules.service';

type FieldConfig = {
  controlName: 'rule' | 'function_name' | 'value';
  label: string;
  type: 'text' | 'select';
  placeholder?: string;
  options?: Array<{ value: string | boolean; label: string }>;
};

@Component({
  selector: 'app-schema-rules',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    MatSnackBarModule,
    MainHeaderComponent
  ],
  templateUrl: './schema-rules.component.html',
  styleUrl: './schema-rules.component.scss'
})
export class SchemaRulesComponent implements OnInit {
  ruleId: string | null = null;
  isEditMode = false;
  isSaving = false;

  form = this.fb.group({
    rule: ['', [Validators.required]],
    function_name: ['', [Validators.required]],
    value: [true, [Validators.required]]
  });

  booleanOptions: Array<{ value: boolean; label: string }> = [
    { value: true, label: 'true' },
    { value: false, label: 'false' }
  ];

  formConfig: FieldConfig[] = [
    {
      controlName: 'rule',
      label: 'Nombre de regla',
      type: 'text',
      placeholder: 'NO_NULO'
    },
    {
      controlName: 'function_name',
      label: 'Nombre de función',
      type: 'text',
      placeholder: 'validar_no_nulo'
    },
    {
      controlName: 'value',
      label: 'Valor',
      type: 'select',
      options: this.booleanOptions
    }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private schemaRulesService: SchemaRulesService,
    private snackBar: MatSnackBar,
    @Optional() private dialogRef: MatDialogRef<SchemaRulesComponent> | null
  ) {}

  ngOnInit(): void {
    this.ruleId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.ruleId;
    if (!this.ruleId) return;

    this.schemaRulesService.getRuleById$(this.ruleId).subscribe({
      next: (rule) => {
        if (!rule) return;
        this.form.patchValue({
          rule: rule.rule,
          function_name: rule.function_name ?? '',
          value: !!rule.value
        });
      },
      error: () => {
        this.snackBar.open('No se pudo cargar la regla.', 'Cerrar', { duration: 4000 });
      }
    });
  }

  get pageTitle(): string {
    return this.isEditMode ? 'Editar regla de schema' : 'Crear regla de schema';
  }

  saveRule(): void {
    if (this.form.invalid || this.isSaving) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const payload: CreateSchemaRulePayload = {
      rule: String(this.form.value.rule ?? '').trim(),
      function_name: String(this.form.value.function_name ?? '').trim(),
      value: !!this.form.value.value
    };

    if (this.isEditMode && this.ruleId) {
      this.schemaRulesService.updateRule$(this.ruleId, payload).subscribe({
        next: () => {
          this.isSaving = false;
          this.snackBar.open('Regla actualizada correctamente.', 'OK', { duration: 3000 });
          this.dialogRef?.close(true);
        },
        error: () => {
          this.isSaving = false;
          this.snackBar.open('No se pudo actualizar la regla.', 'Cerrar', { duration: 4000 });
        }
      });
      return;
    }

    this.schemaRulesService.createRule$(payload).subscribe({
      next: (newId) => {
        this.isSaving = false;
        this.snackBar.open('Regla creada correctamente.', 'OK', { duration: 3000 });
        if (this.dialogRef) {
          this.dialogRef.close(true);
        } else {
          this.router.navigate(['/schema-rules', newId]);
        }
      },
      error: () => {
        this.isSaving = false;
        this.snackBar.open('No se pudo crear la regla.', 'Cerrar', { duration: 4000 });
      }
    });
  }
}
